/**
 * Busca o Endpoint FHIR ativo da organização e extrai WABA / phoneNumberId (rank 1).
 *
 * Query: GET /admin/fhir/Endpoint?organization={oid-cnpj}&status=active
 * Sem Endpoint ativo: canal Fluxmed default (o hook aplica os fallbacks).
 */

import { endpoints, oids } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";
import {
  selectDisplayPhoneFromEndpoint,
  selectPhoneNumberIdFromEndpoint,
  selectWabaIdFromEndpoint,
  type WhatsAppEndpointResource,
} from "../helpers/selectWhatsAppEndpointIds";

export interface EcommerceWhatsAppEndpointConfig {
  wabaIdRank1: string | null;
  phoneNumberIdRank1: string | null;
  /** Número de exibição (contact.value do rank 1), não o ID interno da Meta. */
  displayPhoneRank1: string | null;
  hasActiveMetaEndpoint: boolean;
}

interface FhirBundle {
  resourceType?: string;
  entry?: Array<{ resource?: WhatsAppEndpointResource }>;
}

/**
 * Garante prefixo `${oids.cnpj}-` sem duplicar.
 * Aceita CNPJ cru, id FHIR já prefixado ou `Organization/<id>`.
 */
export function normalizeEcommerceOrganizationId(
  organizationId?: string,
): string | null {
  const trimmed = organizationId?.trim();
  if (!trimmed) return null;

  const withoutRef = trimmed.replace(/^Organization\//i, "");
  const prefix = `${oids.cnpj}-`;
  if (withoutRef.startsWith(prefix)) return withoutRef;
  return `${prefix}${withoutRef}`;
}

function extractEndpointEntries(bundle: FhirBundle): WhatsAppEndpointResource[] {
  return (bundle.entry ?? [])
    .map((entry) => entry.resource)
    .filter(
      (resource): resource is WhatsAppEndpointResource =>
        resource?.resourceType === "Endpoint",
    );
}

const EMPTY_CONFIG: EcommerceWhatsAppEndpointConfig = {
  wabaIdRank1: null,
  phoneNumberIdRank1: null,
  displayPhoneRank1: null,
  hasActiveMetaEndpoint: false,
};

/**
 * Lê o Endpoint ativo e devolve WABA + phoneNumberId do contato rank 1.
 * Retorna `hasActiveMetaEndpoint: false` quando não há Endpoint (não lança).
 */
export async function getEcommerceWhatsAppEndpointConfig(
  organizationId?: string,
): Promise<EcommerceWhatsAppEndpointConfig> {
  const normalizedOrganizationId =
    normalizeEcommerceOrganizationId(organizationId);

  if (!normalizedOrganizationId) return EMPTY_CONFIG;

  const url = `${endpoints.ADMFHIR}/Endpoint?organization=${encodeURIComponent(
    normalizedOrganizationId,
  )}&status=active&_count=50`;

  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
  };

  const response = await useAxios.get<FhirBundle>(url, {
    headers,
    timeout: 20000,
  });

  if (response.status !== HTTPResponseStatus.Success) {
    throw new Error(`Falha ao buscar Endpoint WhatsApp: ${response.status}`);
  }

  const activeEndpoint = extractEndpointEntries(response.data ?? {}).find(
    (candidate) => candidate.status === "active" && Boolean(candidate.id),
  );

  if (!activeEndpoint) return EMPTY_CONFIG;

  return {
    wabaIdRank1: selectWabaIdFromEndpoint(activeEndpoint),
    phoneNumberIdRank1: selectPhoneNumberIdFromEndpoint(activeEndpoint),
    displayPhoneRank1: selectDisplayPhoneFromEndpoint(activeEndpoint),
    hasActiveMetaEndpoint: true,
  };
}
